import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { HookDirective } from '../core/hook';
import { useState } from '../core/hooks';
import { multiSelectComparatorOutput } from '../input/multi-select-number-comparator.component';

type optionType = {
  id: string;
  title: string;
  optionType: string;
  fieldName: string;
};

type Comparator = 'Greater than' | 'Less than' | 'Equal to';

export type superFilterComparator = {
  comparator: Comparator;
  value: number;
};

export type superFilter = optionType & {
  filterId: string;
  valueArr?: string[];
  numberValue?: superFilterComparator;
};

type filterEvent = {
  filters: optionType[];
  changedFilter?: optionType;
};

type filterElement = { [x: string]: string | number };

type filterMap = Map<string, Set<string | number>>;

@Component({
  providers: [],
  selector: 'super-filter',
  templateUrl: './super-filter.component.html',
  styleUrls: ['./super-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperFilterComponent<T> extends HookDirective {
  @Input('data')
  set inputData(data: filterElement[]) {
    if (data != null) {
      this.data = data;
    }
  }
  data: filterElement[];

  @Input()
  options: optionType[];

  @Output()
  readonly filterChanged = new EventEmitter<filterEvent>();

  constructor(readonly cdr: ChangeDetectorRef) {
    super(cdr);
  }

  get _() {
    const [filters, setFilters] = useState<superFilter[]>([]);
    const [doNotRerenderFilters, setDoNotRerenderFilters] = useState<
      superFilter[]
    >([]);
    const [filterValueOptions, setFilterValueOptions] = useState<filterMap>(
      new Map(),
    );
    const optionMap = new Map(this.options.map(option => [option.id, option]));
    const debounceMap: Map<string, NodeJS.Timeout> = new Map();

    const addFilter = (optionId: string) => {
      const option = optionMap.get(optionId)!;
      const filter: superFilter = { ...option, filterId: uuidv4() };
      if (option.optionType === 'dropdown') {
        filter.valueArr = [];
        const filterSet = new Set<string>(filters.map(x => x.fieldName));
        if (filterSet.has(option.fieldName)) {
          return;
        }
      }
      if (option.optionType === 'number') {
        filter.numberValue = { comparator: 'Greater than', value: 0 };
      }
      const newFilters = filters.concat(filter);
      updateFilters(filters, newFilters, filter, true);
    };

    const removeFilter = (optionIndex: number) => {
      const newFilters = filters.filter((_, i) => i !== optionIndex);
      const removedFilter = filters[optionIndex];
      updateFilters(filters, newFilters, removedFilter, true);
    };

    const createFilterOptions = (
      data: filterElement[],
      newFilters: superFilter[],
    ) => {
      const filterMap = CreateSuperFilterMap({ filters: newFilters });
      let filterData = [...data];
      const newFilterOptions: filterMap = new Map();
      for (const filter of newFilters) {
        const filterName = getFilterName(filter);
        if (filter.optionType === 'dropdown') {
          newFilterOptions.set(
            filterName,
            new Set(filterData.map(x => x[filterName])),
          );
          if (filterMap[filterName]) {
            filterData = filterData.filter(x => {
              return (filterMap[filterName] as Set<string>).has(
                x[filter.fieldName] as string,
              );
            });
          }
        }

        if (filter.optionType === 'number' && filterMap[filter.fieldName]) {
          filterData = filterData.filter(x => {
            return numberComparatorFilter(
              { claimCount: x[filter.fieldName] as number },
              filterMap[filterName] as superFilterComparator[],
            );
          });
        }
      }
      return newFilterOptions;
    };

    const getFilterName = (filter: superFilter) => {
      return filter.fieldName;
    };

    const getMultiSelectOptions = (filter: superFilter) => {
      const filterName = getFilterName(filter);
      if (!filterValueOptions.has(filterName)) {
        return [];
      }
      return Array.from(filterValueOptions.get(filterName) ?? []).map(x => ({
        id: x?.toString() ?? '',
        title: x?.toString() ?? '',
      }));
    };

    const numberValueChange = (
      event: multiSelectComparatorOutput,
      filter: superFilter,
      index: number,
    ) => {
      if (debounceMap.has(filter.filterId)) {
        clearTimeout(debounceMap.get(filter.filterId)!);
      }
      debounceMap.set(
        filter.filterId,
        setTimeout(() => {
          if (!filter.numberValue || isNaN(+event.value)) {
            return;
          }
          let value = +event.value;
          if (!value) {
            value = 0;
          }
          const newFilters = [...filters];
          const newFilter = {
            ...filter,
            numberValue: {
              value,
              comparator: event.comparator as Comparator,
            },
          };
          newFilters[index] = newFilter;
          updateFilters(filters, newFilters, newFilter, true);
          debounceMap.delete(filter.filterId);
        }, 1000),
      );
    };

    const setFilterSelection = (
      filterOptions: string[],
      filter: superFilter,
      index: number,
    ) => {
      const newFilters = [...filters];
      const newFilter = { ...filter };
      newFilter.valueArr = [...filterOptions];
      newFilters[index] = newFilter;
      updateFilters(filters, newFilters, newFilter, false);
    };

    const onMultiSelectStateChange = (isOpen: boolean, i: number) => {
      if (!isOpen) {
        updateFilters(
          filters,
          doNotRerenderFilters,
          doNotRerenderFilters[i],
          true,
        );
      }
    };

    const updateFilters = (
      filters: superFilter[],
      newFilters: superFilter[],
      filter: superFilter,
      rerender: boolean,
    ) => {
      let arraysEqual = filters.length === newFilters.length;
      let i = 0;
      while (arraysEqual && i < newFilters.length) {
        arraysEqual =
          arraysEqual && superFilterEquals(filters[i], newFilters[i]);
        i++;
      }
      if (!arraysEqual) {
        setFilterValueOptions(createFilterOptions(this.data, newFilters));
        setDoNotRerenderFilters(newFilters);
        if (rerender) {
          setFilters(newFilters);
        }
        this.filterChanged.next({ filters: newFilters, changedFilter: filter });
      }
    };

    const superFilterEquals = (a: superFilter, b: superFilter) => {
      if (
        b.optionType === 'dropdown' &&
        a.fieldName === b.fieldName &&
        a.valueArr &&
        b.valueArr &&
        a.valueArr.length === b.valueArr.length
      ) {
        const bSorted = b.valueArr.slice().sort();
        return a.valueArr
          .slice()
          .sort()
          .every(function (value, index) {
            return value === bSorted[index];
          });
      }
      if (b.optionType === 'number' && a.numberValue && b.numberValue) {
        return (
          b.numberValue.comparator === a.numberValue.comparator &&
          b.numberValue.value == a.numberValue.value
        );
      }
      return false;
    };

    const getDisableArray = () => {
      const disableArray = [];
      const filterFieldMap = new Map<string, number>();
      filters.forEach(filter => {
        if (filterFieldMap.has(filter.fieldName)) {
          filterFieldMap.set(
            filter.fieldName,
            filterFieldMap.get(filter.fieldName)! + 1,
          );
        } else {
          filterFieldMap.set(filter.fieldName, 1);
        }
      });
      for (const option of options) {
        disableArray.push(
          filterFieldMap.has(option.fieldName) &&
            (option.optionType === 'dropdown' ||
              (option.optionType === 'number' &&
                filterFieldMap.get(option.fieldName)! > 1)),
        );
      }
      return disableArray;
    };

    const [options, _] = useState<optionType[]>(this.options);
    return {
      filters,
      options,
      addFilter,
      removeFilter,
      getMultiSelectOptions,
      getDisableArray,
      setFilterSelection,
      numberValueChange,
      onMultiSelectStateChange,
    };
  }
}

export function CreateSuperFilterMap(filterEvent: { filters: superFilter[] }) {
  const filterMap: { [x: string]: Set<string> | superFilterComparator[] } = {};
  for (const filter of filterEvent.filters) {
    if (filter && IsValidFilter(filter))
      if (filter.optionType === 'dropdown') {
        filterMap[filter.fieldName] = new Set<string>(filter.valueArr);
      } else if (filter.optionType === 'number') {
        if (!filterMap[filter.fieldName]) {
          filterMap[filter.fieldName] = [];
        }
        (filterMap[filter.fieldName] as superFilterComparator[]).push(
          filter.numberValue!,
        );
      }
  }
  return filterMap;
}

export function IsValidFilter(filter: superFilter) {
  return (
    (filter.optionType === 'dropdown' &&
      filter.valueArr &&
      filter.valueArr.length > 0) ||
    filter.optionType === 'number'
  );
}

export function numberComparatorFilter(
  claimInsuranceRow: { claimCount: number },
  claimComparators: superFilterComparator[] | undefined,
) {
  if (claimComparators === undefined) {
    return true;
  }
  let inRange = true;
  for (const claimComparator of claimComparators) {
    inRange =
      inRange &&
      superFilterComparatorMap[claimComparator.comparator](
        claimInsuranceRow,
        +claimComparator.value,
      );
    if (!inRange) {
      break;
    }
  }

  return inRange;
}

const superFilterComparatorMap: {
  [x: string]: (
    claimInsuranceRow: { claimCount: number },
    value: number,
  ) => boolean;
} = {
  'Greater than': (claimInsuranceRow, value) => {
    return claimInsuranceRow.claimCount > value;
  },
  'Less than': (claimInsuranceRow, value) => {
    return claimInsuranceRow.claimCount < value;
  },
  'Equal to': (claimInsuranceRow, value) => {
    return claimInsuranceRow.claimCount === value;
  },
};
